import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { toast, ToastBar, Toaster } from "react-hot-toast";
import useEquipmentStore, {
  equipmentDropdownOptions,
  fetchCategories,
} from "../../../stores/equipment";
import useUserStore from "../../../stores/user";
import Table from "../../../components/Table";
import AddEquipmentCategoryModal from "../components/equipment-category-modal";
import AddEquipmentBrandModal from "../components/equipment-brand-modal";
import DeleteCategoryModal from "./components/delete-category-modal";
import Breadcrumbs from "../../../components/Breadcrumbs";
import DropDown from "../../../components/DropDown/DropDown";
import PlusIcon from "../../../assets/svg/icons/common/Plus";
import styles from "../styles.module.scss";

const EquipmentCategoriesPage = ({ history }) => {
  const isV2Enabled = useUserStore((state) => state.isV2Enabled);
  const categories = useEquipmentStore((state) => state.categories);
  const categoriesLoading = useEquipmentStore((s) => s.categoriesLoading);
  const addEquipmentCategoryModalOpen = useEquipmentStore(
    (s) => s.addEquipmentCategoryModalOpen,
  );
  const activeCategory = useEquipmentStore((s) => s.activeCategory);
  const setActiveCategory = useEquipmentStore((s) => s.setActiveCategory);
  const resetCategories = useEquipmentStore((s) => s.resetCategories);
  const setAddEquipmentCategoryModalOpen = useEquipmentStore(
    (s) => s.setAddEquipmentCategoryModalOpen,
  );
  const addBrandModalOpen = useEquipmentStore((s) => s.addBrandModalOpen);
  const setAddBrandModalOpen = useEquipmentStore((s) => s.setAddBrandModalOpen);
  const [
    deleteEquipmentCategoryModalOpen,
    setDeleteEquipmentCategoryModalOpen,
  ] = useState(false);

  const columns = [
    {
      headerName: "CATEGORY NAME",
      field: "category",
      width: "200px",
      allowSort: false,
    },
    {
      headerName: "DESCRIPTION",
      field: "description",
      allowSort: false,
    },
    {
      headerName: "FREE TEXT",
      field: "free_text_enabled",
      width: "100px",
      allowSort: false,
      renderCell: (value, row) => {
        return value ? "Yes" : "No";
      },
    },
    {
      width: "100px",
      allowSort: false,
      overflow: true,
      renderCell: (value, row) => {
        return (
          <DropDown
            options={[
              {
                text: "Edit",
                onClick: () => {
                  setActiveCategory(row);
                  setAddEquipmentCategoryModalOpen(true);
                },
              },
              {
                text: "Delete",
                onClick: () => {
                  setActiveCategory(row);
                  setDeleteEquipmentCategoryModalOpen(true);
                },
              },
            ]}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M10.5 5.625C10.5 5.32833 10.588 5.03832 10.7528 4.79165C10.9176 4.54497 11.1519 4.35271 11.426 4.23918C11.7001 4.12565 12.0017 4.09594 12.2926 4.15382C12.5836 4.2117 12.8509 4.35456 13.0607 4.56434C13.2704 4.77412 13.4133 5.04139 13.4712 5.33237C13.5291 5.62334 13.4994 5.92494 13.3858 6.19903C13.2723 6.47311 13.08 6.70738 12.8334 6.87221C12.5867 7.03703 12.2967 7.125 12 7.125C11.6022 7.125 11.2206 6.96697 10.9393 6.68566C10.658 6.40436 10.5 6.02283 10.5 5.625ZM12 10.5C11.7033 10.5 11.4133 10.588 11.1666 10.7528C10.92 10.9176 10.7277 11.1519 10.6142 11.426C10.5006 11.7001 10.4709 12.0017 10.5288 12.2926C10.5867 12.5836 10.7296 12.8509 10.9393 13.0607C11.1491 13.2704 11.4164 13.4133 11.7074 13.4712C11.9983 13.5291 12.2999 13.4994 12.574 13.3858C12.8481 13.2723 13.0824 13.08 13.2472 12.8334C13.412 12.5867 13.5 12.2967 13.5 12C13.5 11.6022 13.342 11.2206 13.0607 10.9393C12.7794 10.658 12.3978 10.5 12 10.5ZM12 16.875C11.7033 16.875 11.4133 16.963 11.1666 17.1278C10.92 17.2926 10.7277 17.5269 10.6142 17.801C10.5006 18.0751 10.4709 18.3767 10.5288 18.6676C10.5867 18.9586 10.7296 19.2259 10.9393 19.4357C11.1491 19.6454 11.4164 19.7883 11.7074 19.8462C11.9983 19.9041 12.2999 19.8744 12.574 19.7608C12.8481 19.6473 13.0824 19.455 13.2472 19.2084C13.412 18.9617 13.5 18.6717 13.5 18.375C13.5 17.9772 13.342 17.5956 13.0607 17.3143C12.7794 17.033 12.3978 16.875 12 16.875Z"
                fill="#3A383F"
              />
            </svg>
          </DropDown>
        );
      },
    },
  ];

  useEffect(() => {
    fetchCategories();

    return () => {
      resetCategories();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`${styles.pageContainer} ${styles.pageContainerFullHeight}`}
    >
      <div className={styles.header}>
        <div>
          <Breadcrumbs
            items={[
              {
                label: "Equipment",
                to: isV2Enabled ? "/equipments" : "/v2/equipments",
              },
              {
                label: "Categories",
              },
            ]}
          />
          <h1 className={styles.pageHeading}>Equipment Categories</h1>
        </div>

        <div className={styles.headerActions}>
          <DropDown
            bgColor="#4C70EF"
            textColor="#FFFFFF"
            caretColor="#FFFFFF"
            smallFont
            options={equipmentDropdownOptions(history, isV2Enabled)}
            showArrow
          >
            <PlusIcon />
            Add
          </DropDown>
        </div>
      </div>

      <Table
        layout="fixed"
        noMarginTop
        columns={columns}
        data={categories}
        enableSkeleton
        dataLoading={categoriesLoading}
      />

      <AddEquipmentCategoryModal
        open={addEquipmentCategoryModalOpen}
        editSelectedCat={activeCategory}
        isEdit={!!activeCategory}
        onClose={() => {
          setAddEquipmentCategoryModalOpen(false);
          setTimeout(() => setActiveCategory(null), 200);
        }}
      />

      <AddEquipmentBrandModal
        open={addBrandModalOpen}
        onClose={() => setAddBrandModalOpen(false)}
      />

      <DeleteCategoryModal
        open={deleteEquipmentCategoryModalOpen}
        onClose={() => setDeleteEquipmentCategoryModalOpen(false)}
      />

      <Toaster
        position="top-right"
        containerStyle={{}}
        toastOptions={{
          style: {
            borderRadius: "8px",
            background: "rgba(40, 39, 45, 0.85)",
            color: "#fff",
            padding: "16px 24px",
          },
          duration: 5000,
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <>
                {icon}
                {message}
                {t.type !== "loading" && (
                  <button
                    style={{
                      background: "none",
                      padding: "0",
                      margin: "0",
                      border: "none",
                      justifyContent: "center",
                      display: "flex",
                      alignItems: "center",
                    }}
                    onClick={() => toast.dismiss(t.id)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M12.8535 12.1462C12.9 12.1927 12.9368 12.2478 12.962 12.3085C12.9871 12.3692 13.0001 12.4343 13.0001 12.5C13.0001 12.5657 12.9871 12.6307 12.962 12.6914C12.9368 12.7521 12.9 12.8073 12.8535 12.8537C12.8071 12.9002 12.7519 12.937 12.6912 12.9622C12.6305 12.9873 12.5655 13.0003 12.4998 13.0003C12.4341 13.0003 12.369 12.9873 12.3083 12.9622C12.2476 12.937 12.1925 12.9002 12.146 12.8537L7.99979 8.70685L3.85354 12.8537C3.75972 12.9475 3.63247 13.0003 3.49979 13.0003C3.36711 13.0003 3.23986 12.9475 3.14604 12.8537C3.05222 12.7599 2.99951 12.6327 2.99951 12.5C2.99951 12.3673 3.05222 12.24 3.14604 12.1462L7.29291 7.99997L3.14604 3.85372C3.05222 3.7599 2.99951 3.63265 2.99951 3.49997C2.99951 3.36729 3.05222 3.24004 3.14604 3.14622C3.23986 3.0524 3.36711 2.99969 3.49979 2.99969C3.63247 2.99969 3.75972 3.0524 3.85354 3.14622L7.99979 7.2931L12.146 3.14622C12.2399 3.0524 12.3671 2.99969 12.4998 2.99969C12.6325 2.99969 12.7597 3.0524 12.8535 3.14622C12.9474 3.24004 13.0001 3.36729 13.0001 3.49997C13.0001 3.63265 12.9474 3.7599 12.8535 3.85372L8.70666 7.99997L12.8535 12.1462Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                )}
              </>
            )}
          </ToastBar>
        )}
      </Toaster>
    </div>
  );
};

export default withRouter(EquipmentCategoriesPage);
